import {
  Component,
  Inject,
  Injector,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ChangesetComponent } from '../changeset/changeset.component';
import { combineLatest, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
  MessageService,
  LoaderService,
  GridRowType,
  DataGridComponent,
  ValidationResultService,
} from '@shared';
import { CategoryService, IChangeset } from '@shared-data';
import { CHANGESET_ROW, IPricingItem } from '../../models';
import { RestaurantItemService } from '../../services';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { newFeatures } from '../../constants';

@Component({
  selector: 'app-base-pricing-changeset',
  templateUrl: './base-pricing-changeset.component.html',
  styleUrl: './base-pricing-changeset.component.scss',
})
export class BasePricingChangesetComponent
  extends ChangesetComponent
  implements OnDestroy
{
  locked: boolean = false;

  @ViewChild(DataGridComponent) grid!: DataGridComponent;

  routeParamSubscription!: Subscription;

  newFeatures = newFeatures;

  constructor(
    validationErrorService: ValidationResultService,
    messageService: MessageService,
    loaderService: LoaderService,
    restaurantItemService: RestaurantItemService,
    categoryService: CategoryService,
    private activatedRoute: ActivatedRoute,
    injector: Injector,
    @Inject(CHANGESET_ROW)
    entityType: new (...args: any[]) => GridRowType & IChangeset & IPricingItem,
  ) {
    super(
      validationErrorService,
      messageService,
      restaurantItemService,
      categoryService,
      loaderService,
      injector,
      entityType,
    );
    this.type = 'pricing';

    this.subscribeToRouteParams();
  }

  override setInitialFilter(): void {
    this.initialFilter = {
      logic: 'and',
      filters: [
        {
          field: 'isActive',
          operator: 'eq',
          value: true,
        },
      ],
    }
  }

  subscribeToRouteParams(): void {
    this.routeParamSubscription = combineLatest([
      this.activatedRoute.params,
      this.activatedRoute.queryParams,
    ]).subscribe(([params, queryParams]) => {
      const routeSnapshot = this.activatedRoute.snapshot;
      const newProductTypeId =
        routeSnapshot.url[routeSnapshot.url.length - 1]?.path;
      const newRestaurantId = queryParams['restaurantId'];
      const newCategory = queryParams['category'] ?? null;
      const newLocked = queryParams['locked'] === 'true';

      const isSameContext =
        this.restaurantId === newRestaurantId &&
        this._productTypeId === newProductTypeId &&
        this.category === newCategory;

      this.productTypeId = newProductTypeId;
      this.restaurantId = newRestaurantId;
      this.category = newCategory;
      this.locked = newLocked;

      if (isSameContext && this.state) {
        this.fetchChangesets(this.state);
      } else {
        this.setDefaultState();
      }
    });
  }

  override populateGrid(gridDataResult: GridDataResult): void {
    gridDataResult.data = gridDataResult.data.map((row) => {
      const gridRow = new this.entityType!(row);
      gridRow.locked =
        this.locked ||
        gridRow.availabilityScheduledDate != null ||
        gridRow.pricingScheduledDate != null;

      return gridRow;
    });

    this.changeset = gridDataResult;
  }

  ngOnDestroy(): void {
    this.cache.clear();
    if (this.routeParamSubscription) {
      this.routeParamSubscription.unsubscribe();
    }
  }

  loadRbiRecommendations() {
    if (!this.restaurantId) {
      return;
    }
    this.loaderService.show();
    this.service.loadRbiRecommendations(this.restaurantId, this.productTypeId).subscribe({
      next: () => {
        this.messageService.success('RBI recommendations loaded successfully');
        this.cache.clear();

        this.clearDirtyRows();

        this.fetchChangesets(this.state);

        this.globalEventsService.updatePendingChanges();

        this.loaderService.hide();
      },
      error: (err) => {
        this.loaderService.hide();

        this.messageService.error('Failed to load RBI recommendations');

        var validationError = err?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
      },
    })
  }

  onValueChange(dataItem: { [key: string]: any } & GridRowType, field: string) {
    this.grid.onValueChange(dataItem, field);
  }
}
